import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface OnlineOrder {
  id: string;
  orderNumber: string;
  customer: string;
  email: string;
  items: number;
  total: number;
  paymentMethod: string;
  status: "pending" | "confirmed" | "preparing" | "shipped" | "delivered" | "cancelled";
  date: string;
  time: string;
  shippingMethod: string;
  trackingNumber?: string;
}

const MOCK_ONLINE_ORDERS: OnlineOrder[] = [
  { id: "1", orderNumber: "ONL-2024-001", customer: "John Doe", email: "john@email.com", items: 3, total: 125.99, paymentMethod: "Card", status: "delivered", date: "2024-01-15", time: "10:30 AM", shippingMethod: "Express", trackingNumber: "TRK123456" },
  { id: "2", orderNumber: "ONL-2024-002", customer: "Jane Smith", email: "jane@email.com", items: 5, total: 289.50, paymentMethod: "PayPal", status: "shipped", date: "2024-01-15", time: "11:15 AM", shippingMethod: "Standard", trackingNumber: "TRK789012" },
  { id: "3", orderNumber: "ONL-2024-003", customer: "Mike Wilson", email: "mike@email.com", items: 2, total: 45.00, paymentMethod: "Card", status: "preparing", date: "2024-01-15", time: "12:00 PM", shippingMethod: "Express" },
  { id: "4", orderNumber: "ONL-2024-004", customer: "Sarah Johnson", email: "sarah@email.com", items: 1, total: 89.99, paymentMethod: "Card", status: "confirmed", date: "2024-01-15", time: "02:30 PM", shippingMethod: "Pickup" },
  { id: "5", orderNumber: "ONL-2024-005", customer: "Alex Brown", email: "alex@email.com", items: 4, total: 156.75, paymentMethod: "Card", status: "pending", date: "2024-01-15", time: "04:45 PM", shippingMethod: "Standard" },
  { id: "6", orderNumber: "ONL-2024-006", customer: "Emily Davis", email: "emily@email.com", items: 2, total: 67.00, paymentMethod: "Card", status: "cancelled", date: "2024-01-14", time: "05:20 PM", shippingMethod: "Express" },
];

export default function OnlineOrdersSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [shippingFilter, setShippingFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredOrders = MOCK_ONLINE_ORDERS.filter((order) => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const matchesShipping = shippingFilter === "all" || order.shippingMethod === shippingFilter;
    return matchesSearch && matchesStatus && matchesShipping;
  });

  const getStatusBadge = (status: OnlineOrder["status"]) => {
    const styles = {
      pending: "bg-amber-500/20 text-amber-500",
      confirmed: "bg-blue-500/20 text-blue-500",
      preparing: "bg-violet-500/20 text-violet-500",
      shipped: "bg-cyan-500/20 text-cyan-500",
      delivered: "bg-emerald-500/20 text-emerald-500",
      cancelled: "bg-red-500/20 text-red-500",
    };
    const labels = {
      pending: t("onlineOrders.statusPending"),
      confirmed: t("onlineOrders.statusConfirmed"),
      preparing: t("onlineOrders.statusPreparing"),
      shipped: t("onlineOrders.statusShipped"),
      delivered: t("onlineOrders.statusDelivered"),
      cancelled: t("onlineOrders.statusCancelled"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const pendingOrders = MOCK_ONLINE_ORDERS.filter((o) => o.status === "pending").length;
  const preparingOrders = MOCK_ONLINE_ORDERS.filter((o) => o.status === "preparing").length;
  const shippedOrders = MOCK_ONLINE_ORDERS.filter((o) => o.status === "shipped").length;
  const todayRevenue = MOCK_ONLINE_ORDERS.filter((o) => o.date === "2024-01-15" && o.status !== "cancelled")
    .reduce((acc, o) => acc + o.total, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.onlineOrders")}</h1>
          <p className={themeClasses.textSecondary}>{t("onlineOrders.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-refresh-line"></i>
            {t("onlineOrders.sync")}
          </button>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posOrders.export")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("onlineOrders.pendingOrders")}</span>
            <i className="ri-time-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{pendingOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("onlineOrders.preparingOrders")}</span>
            <i className="ri-box-3-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-violet-500`}>{preparingOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("onlineOrders.shippedOrders")}</span>
            <i className="ri-truck-line text-cyan-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-cyan-500`}>{shippedOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("onlineOrders.todayRevenue")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>${todayRevenue.toFixed(2)}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("onlineOrders.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={shippingFilter}
              onChange={(e) => setShippingFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("onlineOrders.allShipping")}</option>
              <option value="Express">Express</option>
              <option value="Standard">Standard</option>
              <option value="Pickup">Pickup</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("posOrders.allStatuses")}</option>
              <option value="pending">{t("onlineOrders.statusPending")}</option>
              <option value="confirmed">{t("onlineOrders.statusConfirmed")}</option>
              <option value="preparing">{t("onlineOrders.statusPreparing")}</option>
              <option value="shipped">{t("onlineOrders.statusShipped")}</option>
              <option value="delivered">{t("onlineOrders.statusDelivered")}</option>
              <option value="cancelled">{t("onlineOrders.statusCancelled")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.order")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.items")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.total")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("onlineOrders.shipping")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("onlineOrders.tracking")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{order.orderNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{order.date} {order.time}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className={themeClasses.text}>{order.customer}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{order.email}</p>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{order.items}</td>
                  <td className={`px-4 py-3 text-right font-bold ${themeClasses.text}`}>${order.total.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs bg-gray-500/20 ${themeClasses.text}`}>
                      {order.shippingMethod}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-center ${themeClasses.textSecondary}`}>
                    {order.trackingNumber || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(order.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      {order.status === "pending" && (
                        <button className="p-2 rounded-lg hover:bg-emerald-500/10 text-emerald-500">
                          <i className="ri-check-line"></i>
                        </button>
                      )}
                      {order.status === "preparing" && (
                        <button className="p-2 rounded-lg hover:bg-cyan-500/10 text-cyan-500">
                          <i className="ri-truck-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
